/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useMemo } from 'react';
import { Formik, FieldArray, useFormikContext } from 'formik';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  selectComboBoxOptions,
  selectStatusByName,
  selectFormDefinition,
} from '@redux/selectors/FormSelector';
import {
  selectSectionsPerRepeatableSection,
} from '@redux/selectors/ListingSelector';
import Button from '@components/smartAdminUI/Button';
import Badge from '@components/smartAdminUI/Badge';
import { RoomsGridList } from './RoomsGridList';

const RepeatableRoom = ({ roomsPayload }) => {
  const formDefn = useSelector(selectFormDefinition);
  const maxCount = useMemo(() => formDefn?.find(meta => meta.PageName === 'Rooms')?.repeatableMaxCount ?? 25, [formDefn]);

  const formik = useFormikContext();
  const [dimension, setDimension] = useState();
  const roomDimensionOptions = useSelector(state => selectComboBoxOptions(state, 'RoomLengthWidthUnits'));

  const roomErrorDimension = useSelector(state => selectStatusByName(state, 'RoomDimensions'));
  const roomErrorCount = useSelector(state => selectStatusByName(state, 'RoomTypeMinimumRequirements'));
  const roomErrorLevel = useSelector(state => selectStatusByName(state, 'RoomLevel'));
  const roomErrorType = useSelector(state => selectStatusByName(state, 'RoomType'));

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const roomsCount = sectionsPerRepeatableSection.Rooms.length;

  const changeRoomDimension = () => {
    const roomsArr = formik.values.Rooms;

    if (!isEmpty(roomsArr)) {
      let newArr = {};
      Object.entries(roomsArr).forEach(([k]) => {
        newArr = {
          ...newArr,
          [k]: {
            ...roomsArr[k],
            RoomLengthWidthUnits: !isEmpty(dimension) ? dimension.value : '',
          },
        };
      });

      formik.setValues({
        ...formik.values,
        Rooms: {
          ...newArr,
        },
      });
    }
  };

  const buildFormikValues = () => {
    const newValues = {};
    roomsPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(room => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: room.UniqueID,
        RoomLengthWidthUnits: room.RoomLengthWidthUnits,
        RoomLength: room.RoomLength,
        RoomWidth: room.RoomWidth,
        RoomType: room.RoomType,
        RoomLevel: room.RoomLevel,
        SortOrder: room.SortOrder,
        IsDeleted: false,
      };
    });
    return newValues;
  };

  useEffect(() => {
    if (roomsPayload?.length) {
      const newValues = buildFormikValues();
      const formikValues = formik.values;
      formikValues.Rooms = newValues;
      formik.setValues(formikValues);
    }

    if (roomsPayload?.[0]?.RoomLengthWidthUnits === 'F') { // assuming all rooms have the same unit as it should be
      setDimension({
        label: 'Feet',
        parents: undefined,
        value: 'F',
      });
    } else {
      setDimension({
        label: 'Meters',
        parents: undefined,
        value: 'M',
      });
    }
  }, [roomsPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Rooms Exceeded',
    html: `The maximum number of rooms must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addRoom = () => {
    if (roomsCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    let newArr = {};
    const roomsArr = formik.values.Rooms;
    if (!isEmpty(roomsArr)) {
      // Object.entries(roomsArr).filter(r => !r.IsDeleted).forEach(([k], index) => {
      Object.entries(roomsArr).filter(r => !r.IsDeleted).forEach(([k]) => {
        newArr = {
          ...newArr,
          [k]: {
            ...roomsArr[k],
            uid: k,
            // SortOrder: index + 1,
          },
        };
      });
    }

    const roomsValues = newArr ? { ...newArr } : { ...formik.values.Rooms };
    const isLiveCount = Object.values(roomsValues).filter(r => !r.IsDeleted).length;
    formik.setValues({
      ...formik.values,
      Rooms: {
        ...roomsValues,
        [uid]: {
          uid,
          RoomLengthWidthUnits: !isEmpty(dimension) ? dimension.value : '',
          RoomLength: '',
          RoomWidth: '',
          RoomType: '',
          RoomLevel: '',
          SortOrder: isLiveCount + 1,
          IsDeleted: false,
        },
      },
    });
  };

  const customHandler = opt => {
    setDimension(opt);
  };

  useEffect(() => {
    if (isEmpty(formik.values.Rooms)) return;
    changeRoomDimension();
  }, [dimension]);

  return (
    <Formik validateOnChange={false}>
      {() => (
        <>
          <h3 className="fs-xl mb-4">
            Rooms
            <Badge bg="Secondary" marginRight placeRight>
              {roomsCount}
            </Badge>
          </h3>
          <div id="Rooms" key="CardRooms">
            {roomErrorDimension?.errorMessage && <div id="Room Dimensions" className="invalid-feedback">{roomErrorDimension.errorMessage}</div>}
            {roomErrorCount?.errorMessage && <div id="Room Type Minimum Requirements" className="invalid-feedback">{roomErrorCount.errorMessage}</div>}
            {roomErrorLevel?.errorMessage && <div id="Room Level" className="invalid-feedback">{roomErrorLevel.errorMessage}</div>}
            {roomErrorType?.errorMessage && <div id="Room Type" className="invalid-feedback">{roomErrorType.errorMessage}</div>}
            <Button name="btnAddRoom" variant="primary btn-sm float-right" onClick={addRoom}>
              <i className="fas fa-plus mr-2" />
              Add Room
            </Button>
            <br className="clearfix" />
            <FieldArray name="Rooms">
              <>
                <div className="form-group mt-4">
                  <label
                    className="form-label"
                    htmlFor="RoomLengthWidthUnits"
                  >
                    Units
                  </label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    id="RoomLengthWidthUnits"
                    onChange={customHandler}
                    options={roomDimensionOptions}
                    value={dimension}
                  />
                </div>
                { roomsCount === 0 && (
                  <p className="text-center alert alert-info mt-0 mb-2 wobble-hor-bottom">
                    <i className="far fa-info-circle mr-2" />
                    Please note that you can use the dropdown above to change units from Imperial to Metric.
                  </p>
                )}
                {!isEmpty(formik.values.Rooms) && (
                  <RoomsGridList
                    formik={formik}
                    handle
                  />
                )}
              </>
            </FieldArray>
            {(roomsCount > 2) && (
              <>
                <Button name="btnAddRoom" variant="primary ml-auto float-right" onClick={addRoom}>
                  <i className="fas fa-plus mr-2" />
                  Add Room
                </Button>
                <br className="clearfix" />
              </>
            )}
          </div>
        </>
      )}
    </Formik>
  );
};

RepeatableRoom.propTypes = {
  roomsPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableRoom;
